import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function useStateParams<T>(
  initialState: Partial<T>,
  paramsName: string,
  serialize: (state: T) => string = JSON.stringify,
  deserialize: (state: string) => T = JSON.parse,
): [Partial<T>, (state: T) => void] {
  const location = useLocation();
  const navigate = useNavigate();
  const search = new URLSearchParams(location.search);

  const existingValue = search.get(paramsName);
  const [state, setState] = useState<Partial<T>>(
    existingValue ? deserialize(existingValue) : initialState,
  );

  useEffect(() => {
    if (existingValue && deserialize(existingValue) !== state) {
      setState(deserialize(existingValue));
    }
  }, [existingValue]);

  const onChange = useCallback(
    (s: T) => {
      setState(s);
      const searchParams = new URLSearchParams(location.search);
      searchParams.set(paramsName, serialize(s));
      const pathname = location.pathname;
      navigate(`${pathname}?${searchParams.toString()}`, { replace: true });
    },
    [location.pathname, location.search, navigate, paramsName],
  );

  return [state, onChange];
}
